import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  AssignmentRounded,
  AutoAwesomeRounded,
  CalendarMonthRounded,
  DeleteRounded,
  DescriptionRounded,
  OpenInNewRounded,
  SearchRounded,
  TuneRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowCase,
  deleteIndustrialWorkflowCase,
  getIndustrialWorkflowOperationsDashboard,
  getIndustrialWorkflowsContext,
  listIndustrialWorkflowAssignableUsers,
  listIndustrialWorkflowCases,
  listIndustrialWorkflowFields,
  listIndustrialWorkflowProcesses,
  listIndustrialWorkflowStages,
  updateIndustrialWorkflowCase,
} from '../services/industrialWorkflowsService';
import IndustrialWorkflowCaseTasksDialog from '../components/IndustrialWorkflowCaseTasksDialog';
import IndustrialWorkflowCaseHistoryDialog from '../components/IndustrialWorkflowCaseHistoryDialog';
import IndustrialWorkflowsOperationsDashboard from '../components/IndustrialWorkflowsOperationsDashboard';
import IndustrialWorkflowDatePicker, {
  formatOperationalRelativeDay,
  getOperationalDayDifference,
  toDateOnlyInputValue,
  toEndOfLocalDayIso,
} from '../components/IndustrialWorkflowDatePicker';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseCreatePayload,
  IndustrialWorkflowCasePriority,
  IndustrialWorkflowCaseUpdatePayload,
  IndustrialWorkflowFieldDefinition,
  IndustrialWorkflowStageDefinition,
} from '../types/industrialWorkflows';

type CaseFormState = {
  title: string;
  description: string;
  priority: IndustrialWorkflowCasePriority;
  assigned_user_id: string;
  due_at: string;
  field_values: Record<string, unknown>;
};

type DueStatusFilter =
  | ''
  | 'overdue'
  | 'today'
  | 'upcoming'
  | 'without_due';

type OperationsView =
  | 'summary'
  | 'my-work'
  | 'cases'
  | 'areas'
  | 'follow-ups';

type FieldDisplayTier =
  | 'primary'
  | 'secondary'
  | 'technical';

const OPERATIONS_VIEWS: Array<{
  value: OperationsView;
  label: string;
}> = [
  {
    value: 'summary',
    label: 'Resumen',
  },
  {
    value: 'my-work',
    label: 'Mi trabajo',
  },
  {
    value: 'cases',
    label: 'Expedientes',
  },
  {
    value: 'areas',
    label: 'Áreas',
  },
  {
    value: 'follow-ups',
    label: 'Seguimientos',
  },
];

function getInitialOperationsView(): OperationsView {
  if (typeof window === 'undefined') {
    return 'cases';
  }

  const requestedView = new URLSearchParams(
    window.location.search,
  ).get('iw_tab');

  return OPERATIONS_VIEWS.some(
    (view) => view.value === requestedView,
  )
    ? (requestedView as OperationsView)
    : 'cases';
}

function getInitialSearchText() {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    new URLSearchParams(
      window.location.search,
    ).get('iw_q') ?? ''
  );
}

function getInitialProcessId() {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    new URLSearchParams(
      window.location.search,
    ).get('iw_process') ?? ''
  );
}

const EMPTY_CASE_FORM: CaseFormState = {
  title: '',
  description: '',
  priority: 'normal',
  assigned_user_id: '',
  due_at: '',
  field_values: {},
};

const WITHOUT_OPERATIONAL_AREA_VALUE =
  '__without_operational_area__';

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function getPriorityLabel(priority: IndustrialWorkflowCasePriority) {
  if (priority === 'urgent') return 'Urgente';
  if (priority === 'high') return 'Alta';
  if (priority === 'low') return 'Baja';

  return 'Normal';
}

function getPriorityTone(priority: IndustrialWorkflowCasePriority) {
  if (priority === 'urgent') {
    return {
      color: '#fecaca',
      bgcolor: alpha('#ef4444', 0.18),
    };
  }

  if (priority === 'high') {
    return {
      color: '#fed7aa',
      bgcolor: alpha('#f97316', 0.16),
    };
  }

  if (priority === 'low') {
    return {
      color: '#cbd5e1',
      bgcolor: alpha('#64748b', 0.15),
    };
  }

  return {
    color: '#bfdbfe',
    bgcolor: alpha('#3b82f6', 0.15),
  };
}

function formatDate(
  value: string | null | undefined,
) {
  if (!value) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
  }).format(new Date(value));
}

function formatFieldValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

function getFieldDisplayTier(
  field: IndustrialWorkflowFieldDefinition,
): FieldDisplayTier {
  const displayTier =
    field.settings?.display_tier;

  if (
    displayTier === 'primary'
    || displayTier === 'secondary'
    || displayTier === 'technical'
  ) {
    return displayTier;
  }

  return field.visible_in_card
    ? 'primary'
    : 'secondary';
}


function getTitlePart(value: unknown) {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(' · ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : '';
  }

  return String(value).trim();
}

function normalizeFieldIdentity(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function toDateTimeLocalValue(value: string | null) {
  if (!value) return '';

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const timezoneOffset =
    parsedDate.getTimezoneOffset() * 60 * 1000;

  return new Date(
    parsedDate.getTime() - timezoneOffset,
  )
    .toISOString()
    .slice(0, 16);
}

function getOperationalDueStatus(
  workflowCase: IndustrialWorkflowCase,
): DueStatusFilter {
  const differenceInDays =
    getOperationalDayDifference(
      workflowCase.operational_due_at,
    );

  if (differenceInDays === null) {
    return 'without_due';
  }

  if (differenceInDays < 0) {
    return 'overdue';
  }

  if (differenceInDays === 0) {
    return 'today';
  }

  return 'upcoming';
}

function getOperationalStageTone(
  workflowCase: IndustrialWorkflowCase,
) {
  const dueStatus =
    getOperationalDueStatus(workflowCase);

  const relativeText =
    formatOperationalRelativeDay(
      workflowCase.operational_due_at,
    );

  const isExternalWait =
    workflowCase.current_stage_type
    === 'outside_system_wait';

  const isFollowUpReview =
    isExternalWait
    && Boolean(
      workflowCase.next_review_at,
    );

  if (dueStatus === 'overdue') {
    return {
      color: '#fecaca',
      bgcolor: alpha('#ef4444', 0.16),
      label: isFollowUpReview
        ? `Seguimiento vencido · ${relativeText ?? 'revisar'}`
        : isExternalWait
          ? `Respuesta vencida · ${relativeText ?? 'revisar'}`
          : `Vencida · ${relativeText ?? 'revisar'}`,
    };
  }

  if (dueStatus === 'today') {
    return {
      color: '#fde68a',
      bgcolor: alpha('#f59e0b', 0.16),
      label: isFollowUpReview
        ? 'Seguimiento hoy'
        : isExternalWait
          ? 'Respuesta hoy'
          : 'Vence hoy',
    };
  }

  if (dueStatus === 'upcoming') {
    return {
      color: '#bfdbfe',
      bgcolor: alpha('#3b82f6', 0.14),
      label: isFollowUpReview
        ? `Seguimiento ${relativeText ?? 'próximamente'}`
        : isExternalWait
          ? `Respuesta ${relativeText ?? 'próximamente'}`
          : relativeText === 'mañana'
            ? 'Vence mañana'
            : `Quedan ${
                relativeText?.replace(
                  /^en /,
                  '',
                )
                ?? 'varios días'
              }`,
    };
  }

  return {
    color: '#cbd5e1',
    bgcolor: alpha('#64748b', 0.12),
    label: isExternalWait
      ? 'Sin fecha de respuesta'
      : 'Sin plazo configurado',
  };
}

function getCaseTargetDateTone(
  value: string | null,
) {
  if (!value) {
    return {
      color: '#94a3b8',
      bgcolor: alpha('#64748b', 0.1),
      label: 'Sin objetivo',
    };
  }

  const targetDate = new Date(value);

  if (Number.isNaN(targetDate.getTime())) {
    return {
      color: '#94a3b8',
      bgcolor: alpha('#64748b', 0.1),
      label: 'Fecha no disponible',
    };
  }

  const now = new Date();

  if (targetDate.getTime() < now.getTime()) {
    return {
      color: '#fecaca',
      bgcolor: alpha('#ef4444', 0.16),
      label: formatDate(value),
    };
  }

  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  if (
    targetDate.getTime()
    <= endOfToday.getTime()
  ) {
    return {
      color: '#fde68a',
      bgcolor: alpha('#f59e0b', 0.16),
      label: formatDate(value),
    };
  }

  return {
    color: '#cbd5e1',
    bgcolor: alpha('#64748b', 0.1),
    label: formatDate(value),
  };
}

function getStageGroupContextLabel(
  stage: IndustrialWorkflowStageDefinition | undefined,
  workflowCase: IndustrialWorkflowCase | undefined,
) {
  if (!stage) {
    return 'Etapa no disponible';
  }

  if (
    stage.stage_type === 'closure'
    || stage.is_final
  ) {
    return 'Cierre';
  }

  const areaName =
    workflowCase
      ?.operational_work_area_name;

  if (
    stage.stage_type
    === 'outside_system_wait'
  ) {
    return areaName
      ? `Espera externa · Seguimiento · ${areaName}`
      : 'Espera externa';
  }

  return areaName
    ? `Trabajo interno · Ejecuta · ${areaName}`
    : 'Trabajo interno';
}

function buildEditableFieldValues(
  workflowCase: IndustrialWorkflowCase,
  fields: IndustrialWorkflowFieldDefinition[],
) {
  return Object.fromEntries(
    fields
      .filter(
        (field) =>
          field.is_active &&
          Object.prototype.hasOwnProperty.call(
            workflowCase.field_values,
            field.code,
          ),
      )
      .map((field) => {
        const value =
          workflowCase.field_values[field.code];

        if (
          field.field_type === 'datetime' &&
          typeof value === 'string'
        ) {
          return [
            field.code,
            toDateTimeLocalValue(value),
          ];
        }

        if (
          field.field_type === 'date' &&
          typeof value === 'string'
        ) {
          return [
            field.code,
            value.slice(0, 10),
          ];
        }

        return [field.code, value];
      }),
  );
}

function buildEditCaseForm(
  workflowCase: IndustrialWorkflowCase,
  fields: IndustrialWorkflowFieldDefinition[],
): CaseFormState {
  return {
    title: workflowCase.title,
    description: workflowCase.description ?? '',
    priority: workflowCase.priority,
    assigned_user_id:
      workflowCase.assigned_user_id ?? '',
    due_at: toDateOnlyInputValue(
      workflowCase.due_at,
    ),
    field_values: buildEditableFieldValues(
      workflowCase,
      fields,
    ),
  };
}

function buildCasePayload(
  processDefinitionId: string,
  form: CaseFormState,
): IndustrialWorkflowCaseCreatePayload {
  return {
    process_definition_id: processDefinitionId,
    title: form.title,
    description: form.description.trim() || null,
    priority: form.priority,
    assigned_user_id: form.assigned_user_id || null,
    due_at: form.due_at
      ? toEndOfLocalDayIso(form.due_at)
      : null,
    field_values: form.field_values,
  };
}

function buildCaseUpdatePayload(
  form: CaseFormState,
): IndustrialWorkflowCaseUpdatePayload {
  return {
    title: form.title,
    description: form.description.trim() || null,
    priority: form.priority,
    assigned_user_id: form.assigned_user_id || null,
    due_at: form.due_at
      ? toEndOfLocalDayIso(form.due_at)
      : null,
    field_values: form.field_values,
  };
}

function DynamicFieldInput({
  field,
  value,
  onChange,
}: {
  field: IndustrialWorkflowFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (field.field_type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
          />
        }
        label={field.label}
      />
    );
  }

  if (field.field_type === 'select') {
    return (
      <TextField
        select
        label={field.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        required={field.is_required_on_create}
        helperText={field.description ?? undefined}
      >
        {(field.options ?? []).map((option) => (
          <MenuItem
            key={String(option)}
            value={String(option)}
          >
            {String(option)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.field_type === 'multi_select') {
    return (
      <TextField
        label={field.label}
        value={
          Array.isArray(value)
            ? value.join('\n')
            : ''
        }
        onChange={(event) =>
          onChange(
            event.target.value
              .split('\n')
              .map((item) => item.trim())
              .filter(Boolean),
          )
        }
        multiline
        minRows={3}
        fullWidth
        required={field.is_required_on_create}
        helperText={
          field.description ??
          'Introduce un valor por línea.'
        }
      />
    );
  }

  if (field.field_type === 'date') {
    return (
      <IndustrialWorkflowDatePicker
        label={field.label}
        value={
          typeof value === 'string'
            ? value
            : ''
        }
        onChange={onChange}
        helperText={
          field.description
          ?? undefined
        }
        required={
          field.is_required_on_create
        }
      />
    );
  }

  const inputType =
    field.field_type === 'integer' ||
    field.field_type === 'number' ||
    field.field_type === 'currency'
      ? 'number'
      : field.field_type === 'datetime'
        ? 'datetime-local'
          : field.field_type === 'email'
            ? 'email'
            : field.field_type === 'url'
              ? 'url'
              : 'text';

  return (
    <TextField
      label={field.label}
      value={
        typeof value === 'string' ||
        typeof value === 'number'
          ? value
          : ''
      }
      onChange={(event) => onChange(event.target.value)}
      type={inputType}
      fullWidth
      multiline={field.field_type === 'long_text'}
      minRows={field.field_type === 'long_text' ? 3 : undefined}
      required={field.is_required_on_create}
      helperText={field.description ?? undefined}
      InputLabelProps={
        field.field_type === 'datetime'
          ? {
              shrink: true,
            }
          : undefined
      }
    />
  );
}

export default function IndustrialWorkflowsCasesView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedProcessId, setSelectedProcessId] =
    useState<string>('');
  const [activeView, setActiveView] =
    useState<OperationsView>(getInitialOperationsView);
  const [searchText, setSearchText] =
    useState(getInitialSearchText);
  const [debouncedSearchText, setDebouncedSearchText] =
    useState(getInitialSearchText);
  const [selectedStageFilter, setSelectedStageFilter] =
    useState('');
  const [selectedPriorityFilter, setSelectedPriorityFilter] =
    useState<IndustrialWorkflowCasePriority | ''>('');
  const [selectedWorkAreaFilter, setSelectedWorkAreaFilter] =
    useState('');
  const [selectedDueStatusFilter, setSelectedDueStatusFilter] =
    useState<DueStatusFilter>('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [tasksCase, setTasksCase] =
    useState<IndustrialWorkflowCase | null>(null);
  const [historyCase, setHistoryCase] =
    useState<IndustrialWorkflowCase | null>(null);
  const [editingCase, setEditingCase] =
    useState<IndustrialWorkflowCase | null>(null);
  const [casePendingDelete, setCasePendingDelete] =
    useState<IndustrialWorkflowCase | null>(null);
  const [
    deleteReferenceCodeConfirmation,
    setDeleteReferenceCodeConfirmation,
  ] = useState('');
  const [caseForm, setCaseForm] =
    useState<CaseFormState>(EMPTY_CASE_FORM);

  const processesQuery = useQuery({
    queryKey: ['industrial-workflows-processes'],
    queryFn: () => listIndustrialWorkflowProcesses(),
  });

  const workflowsContextQuery = useQuery({
    queryKey: ['industrial-workflows-context'],
    queryFn: getIndustrialWorkflowsContext,
  });

  const workflowsPermissions = new Set(
    workflowsContextQuery.data?.permissions ?? [],
  );

  const canOperateTasks =
    workflowsPermissions.has('workflows:tasks:operate') ||
    workflowsPermissions.has('workflows:tasks:manage');

  const canManageTasks = workflowsPermissions.has(
    'workflows:tasks:manage',
  );

  const canManageCases = workflowsPermissions.has(
    'workflows:cases:manage',
  );

  const assignableUsersQuery = useQuery({
    queryKey: ['industrial-workflows-assignable-users'],
    queryFn: listIndustrialWorkflowAssignableUsers,
  });

  useEffect(() => {
    const processes = processesQuery.data ?? [];

    if (!processes.length) {
      if (selectedProcessId) {
        setSelectedProcessId('');
      }

      return;
    }

    if (
      !selectedProcessId ||
      !processes.some(
        (process) => process.id === selectedProcessId,
      )
    ) {
      const requestedProcessId =
        getInitialProcessId();

      setSelectedProcessId(
        processes.some(
          (process) =>
            process.id
            === requestedProcessId,
        )
          ? requestedProcessId
          : processes[0].id,
      );
    }
  }, [processesQuery.data, selectedProcessId]);

  useEffect(() => {
    setSelectedStageFilter('');
    setSelectedPriorityFilter('');
    setSelectedWorkAreaFilter('');
    setSelectedDueStatusFilter('');
  }, [selectedProcessId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const searchParams = new URLSearchParams(
      window.location.search,
    );

    searchParams.set('iw_tab', activeView);

    if (searchText.trim()) {
      searchParams.set('iw_q', searchText);
    } else {
      searchParams.delete('iw_q');
    }

    if (selectedProcessId) {
      searchParams.set(
        'iw_process',
        selectedProcessId,
      );
    } else {
      searchParams.delete('iw_process');
    }

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${searchParams.toString()}${window.location.hash}`,
    );
  }, [
    activeView,
    searchText,
    selectedProcessId,
  ]);

  const stagesQuery = useQuery({
    queryKey: [
      'industrial-workflows-stages',
      selectedProcessId,
    ],
    queryFn: () =>
      listIndustrialWorkflowStages(selectedProcessId),
    enabled: Boolean(selectedProcessId),
  });

  const fieldsQuery = useQuery({
    queryKey: [
      'industrial-workflows-fields',
      selectedProcessId,
    ],
    queryFn: () =>
      listIndustrialWorkflowFields(selectedProcessId),
    enabled: Boolean(selectedProcessId),
  });

  const casesQuery = useQuery({
    queryKey: [
      'industrial-workflows-cases',
      selectedProcessId,
      'open',
    ],
    queryFn: () =>
      listIndustrialWorkflowCases({
        process_definition_id: selectedProcessId,
        case_status: 'open',
      }),
    enabled: Boolean(selectedProcessId),
  });

  const operationsDashboardQuery = useQuery({
    queryKey: [
      'industrial-workflows-operations-dashboard',
      selectedProcessId,
      debouncedSearchText,
    ],
    queryFn: () =>
      getIndustrialWorkflowOperationsDashboard(
        selectedProcessId,
        debouncedSearchText,
      ),
    enabled:
      Boolean(selectedProcessId)
      && activeView !== 'cases',
  });

  const orderedStages = useMemo(
    () =>
      [...(stagesQuery.data ?? [])].sort(
        (left, right) =>
          left.display_order - right.display_order ||
          left.name.localeCompare(right.name),
      ),
    [stagesQuery.data],
  );

  const activeFields = useMemo(
    () =>
      [...(fieldsQuery.data ?? [])]
        .filter((field) => field.is_active)
        .sort(
          (left, right) =>
            left.display_order - right.display_order ||
            left.label.localeCompare(right.label),
        ),
    [fieldsQuery.data],
  );

  const visibleListFields = useMemo(
    () =>
      activeFields.filter(
        (field) => field.visible_in_list,
      ),
    [activeFields],
  );


  const stageNameById = useMemo(
    () =>
      new Map(
        orderedStages.map((stage) => [
          stage.id,
          stage.name,
        ]),
      ),
    [orderedStages],
  );

  const operationalWorkAreas = useMemo(
    () => {
      const areaNameById = new Map<
        string,
        string
      >();

      (casesQuery.data ?? []).forEach(
        (workflowCase) => {
          if (
            workflowCase
              .operational_work_area_id
            && workflowCase
              .operational_work_area_name
          ) {
            areaNameById.set(
              workflowCase
                .operational_work_area_id,
              workflowCase
                .operational_work_area_name,
            );
          }
        },
      );

      return [
        ...areaNameById.entries(),
      ]
        .map(([id, name]) => ({
          id,
          name,
        }))
        .sort(
          (left, right) =>
            left.name.localeCompare(
              right.name,
            ),
        );
    },
    [casesQuery.data],
  );

  const titleSuggestions = useMemo(() => {
    const clientField = activeFields.find(
      (field) =>
        normalizeFieldIdentity(field.code) === 'CLIENTE' ||
        normalizeFieldIdentity(field.label) === 'CLIENTE',
    );

    if (!clientField) {
      return [];
    }

    const clientValue = getTitlePart(
      caseForm.field_values[clientField.code],
    );

    if (!clientValue) {
      return [];
    }

    const suggestions = [
      {
        key: clientField.id,
        label: clientField.label,
        value: clientValue,
      },
    ];

    activeFields.forEach((field) => {
      if (field.id === clientField.id) {
        return;
      }

      const fieldValue = getTitlePart(
        caseForm.field_values[field.code],
      );

      if (!fieldValue) {
        return;
      }

      suggestions.push({
        key: `${field.id}-${clientField.id}`,
        label: `${field.label} · ${clientField.label}`,
        value: `${fieldValue} · ${clientValue}`,
      });
    });

    return suggestions;
  }, [
    activeFields,
    caseForm.field_values,
  ]);

  const filteredCases = useMemo(() => {
    const normalizedSearchText = searchText
      .trim()
      .toLocaleLowerCase('es');

    return (casesQuery.data ?? []).filter((workflowCase) => {
      if (
        selectedStageFilter &&
        workflowCase.current_stage_definition_id !==
          selectedStageFilter
      ) {
        return false;
      }

      if (
        selectedPriorityFilter &&
        workflowCase.priority !== selectedPriorityFilter
      ) {
        return false;
      }

      if (selectedWorkAreaFilter) {
        if (
          selectedWorkAreaFilter
          === WITHOUT_OPERATIONAL_AREA_VALUE
        ) {
          if (
            workflowCase
              .operational_work_area_id
          ) {
            return false;
          }
        } else if (
          workflowCase
            .operational_work_area_id
          !== selectedWorkAreaFilter
        ) {
          return false;
        }
      }

      if (
        selectedDueStatusFilter
        && getOperationalDueStatus(
          workflowCase,
        ) !== selectedDueStatusFilter
      ) {
        return false;
      }

      if (!normalizedSearchText) {
        return true;
      }

      const searchableValues = [
        workflowCase.title,
        workflowCase.reference_code,
        workflowCase.description ?? '',
        workflowCase
          .operational_work_area_name
          ?? '',
        stageNameById.get(
          workflowCase
            .current_stage_definition_id,
        ) ?? '',
        ...Object.values(
          workflowCase.field_values ?? {},
        ).map(formatFieldValue),
      ]
        .join(' ')
        .toLocaleLowerCase('es');

      return searchableValues.includes(normalizedSearchText);
    });
  }, [
    casesQuery.data,
    searchText,
    selectedDueStatusFilter,
    selectedPriorityFilter,
    selectedStageFilter,
    selectedWorkAreaFilter,
    stageNameById,
  ]);

  const groupedCases = useMemo(() => {
    const groupedByStageId = new Map<
      string,
      IndustrialWorkflowCase[]
    >();

    filteredCases.forEach((workflowCase) => {
      const stageId =
        workflowCase.current_stage_definition_id;

      const existingCases =
        groupedByStageId.get(stageId) ?? [];

      existingCases.push(workflowCase);
      groupedByStageId.set(stageId, existingCases);
    });

    const sortCases = (
      cases: IndustrialWorkflowCase[],
    ) =>
      [...cases].sort((left, right) => {
        const leftDueAt =
          left.operational_due_at
            ? new Date(
                left.operational_due_at,
              ).getTime()
            : Number.POSITIVE_INFINITY;

        const rightDueAt =
          right.operational_due_at
            ? new Date(
                right.operational_due_at,
              ).getTime()
            : Number.POSITIVE_INFINITY;

        return (
          leftDueAt - rightDueAt ||
          left.title.localeCompare(right.title)
        );
      });

    const knownStageGroups: Array<{
      stage: IndustrialWorkflowStageDefinition | undefined;
      cases: IndustrialWorkflowCase[];
    }> = orderedStages
      .map((stage) => ({
        stage,
        cases: sortCases(
          groupedByStageId.get(stage.id) ?? [],
        ),
      }))
      .filter((group) => group.cases.length);

    const knownStageIds = new Set(
      orderedStages.map((stage) => stage.id),
    );

    const unknownCases = sortCases(
      filteredCases.filter(
        (workflowCase) =>
          !knownStageIds.has(
            workflowCase.current_stage_definition_id,
          ),
      ),
    );

    if (unknownCases.length) {
      knownStageGroups.push({
        stage: undefined,
        cases: unknownCases,
      });
    }

    return knownStageGroups;
  }, [filteredCases, orderedStages]);

  const listGridTemplateColumns = useMemo(
    () =>
      [
        'minmax(240px, 1.8fr)',
        ...visibleListFields.map(
          () => 'minmax(150px, 1fr)',
        ),
        'minmax(220px, 1.1fr)',
        'minmax(135px, 0.75fr)',
        'minmax(110px, 0.7fr)',
        'minmax(150px, auto)',
      ].join(' '),
    [visibleListFields],
  );

  const listMinWidth = useMemo(
    () => 855 + visibleListFields.length * 150,
    [visibleListFields.length],
  );

  const hasActiveFilters = Boolean(
    searchText ||
      selectedStageFilter ||
      selectedPriorityFilter ||
      selectedWorkAreaFilter ||
      selectedDueStatusFilter,
  );

  const createCaseMutation = useMutation({
    mutationFn: (
      payload: IndustrialWorkflowCaseCreatePayload,
    ) => createIndustrialWorkflowCase(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-cases',
          selectedProcessId,
        ],
      });

      setDialogOpen(false);
      setEditingCase(null);
      setCaseForm(EMPTY_CASE_FORM);

      enqueueSnackbar(
        'Expediente creado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: ({
      caseId,
      payload,
    }: {
      caseId: string;
      payload: IndustrialWorkflowCaseUpdatePayload;
    }) => updateIndustrialWorkflowCase(caseId, payload),
    onSuccess: async (updatedCase) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-cases',
            selectedProcessId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-case-events',
            updatedCase.id,
          ],
        }),
      ]);

      if (tasksCase?.id === updatedCase.id) {
        setTasksCase(updatedCase);
      }

      if (historyCase?.id === updatedCase.id) {
        setHistoryCase(updatedCase);
      }

      setDialogOpen(false);
      setEditingCase(null);
      setCaseForm(EMPTY_CASE_FORM);

      enqueueSnackbar(
        'Expediente actualizado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const deleteCaseMutation = useMutation({
    mutationFn: ({
      caseId,
      referenceCode,
    }: {
      caseId: string;
      referenceCode: string;
    }) =>
      deleteIndustrialWorkflowCase(
        caseId,
        referenceCode,
      ),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-cases',
          selectedProcessId,
        ],
      });

      if (tasksCase?.id === variables.caseId) {
        setTasksCase(null);
      }

      if (historyCase?.id === variables.caseId) {
        setHistoryCase(null);
      }

      setCasePendingDelete(null);
      setDeleteReferenceCodeConfirmation('');
      setDialogOpen(false);
      setEditingCase(null);
      setCaseForm(EMPTY_CASE_FORM);

      enqueueSnackbar(
        'Expediente eliminado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const handleCloseCaseDialog = () => {
    if (deleteCaseMutation.isPending) {
      return;
    }

    setDialogOpen(false);
    setEditingCase(null);
    setCaseForm(EMPTY_CASE_FORM);
  };

  const handleCreateCase = () => {
    setEditingCase(null);
    setCaseForm(EMPTY_CASE_FORM);
    setDialogOpen(true);
  };

  const handleEditCase = (
    workflowCase: IndustrialWorkflowCase,
  ) => {
    setEditingCase(workflowCase);
    setCaseForm(
      buildEditCaseForm(
        workflowCase,
        activeFields,
      ),
    );
    setDialogOpen(true);
  };

  const handleConfirmDeleteCase = () => {
    if (!casePendingDelete) {
      return;
    }

    if (
      deleteReferenceCodeConfirmation.trim() !==
      casePendingDelete.reference_code
    ) {
      enqueueSnackbar(
        'Escribe la referencia exacta del expediente para confirmar.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    deleteCaseMutation.mutate({
      caseId: casePendingDelete.id,
      referenceCode:
        deleteReferenceCodeConfirmation.trim(),
    });
  };

  const handleSaveCase = () => {
    if (editingCase) {
      updateCaseMutation.mutate({
        caseId: editingCase.id,
        payload: buildCaseUpdatePayload(caseForm),
      });

      return;
    }

    if (!selectedProcessId) {
      enqueueSnackbar(
        'Selecciona primero un proceso.',
        {
          variant: 'error',
        },
      );

      return;
    }

    createCaseMutation.mutate(
      buildCasePayload(
        selectedProcessId,
        caseForm,
      ),
    );
  };

  const handleResetFilters = () => {
    setSearchText('');
    setSelectedStageFilter('');
    setSelectedPriorityFilter('');
    setSelectedWorkAreaFilter('');
    setSelectedDueStatusFilter('');
  };

  return (
    <Stack spacing={2.5}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#38bdf8', 0.18)}`,
          bgcolor: 'rgba(15,23,42,0.78)',
          p: { xs: 2.25, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Stack spacing={0.65}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <AssignmentRounded sx={{ color: '#38bdf8' }} />

              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  fontSize: '1.45rem',
                }}
              >
                Expedientes
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: '#94a3b8',
                maxWidth: 820,
              }}
            >
              Consulta el estado del flujo y abre cada expediente para
              registrar trabajo, revisar su historial o continuar a la
              siguiente etapa.
            </Typography>
          </Stack>

          {canManageCases ? (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              disabled={!selectedProcessId}
              onClick={handleCreateCase}
              sx={{
                fontWeight: 900,
                textTransform: 'none',
              }}
            >
              Nuevo expediente
            </Button>
          ) : null}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.14)}`,
          bgcolor: 'rgba(15,23,42,0.66)',
          p: { xs: 1.75, md: 2.25 },
        }}
      >
        <Stack spacing={2}>
          <TextField
            select
            label="Proceso"
            value={selectedProcessId}
            onChange={(event) =>
              setSelectedProcessId(event.target.value)
            }
            fullWidth
          >
            {(processesQuery.data ?? []).map((process) => (
              <MenuItem
                key={process.id}
                value={process.id}
              >
                {process.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Buscar"
            value={searchText}
            onChange={(event) =>
              setSearchText(event.target.value)
            }
            placeholder="Buscar en expedientes y trabajo relacionado..."
            InputProps={{
              startAdornment: (
                <SearchRounded
                  sx={{
                    color: '#64748b',
                    mr: 0.8,
                  }}
                />
              ),
            }}
            fullWidth
          />

          <Tabs
            value={activeView}
            onChange={(_, value: OperationsView) =>
              setActiveView(value)
            }
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 38,
              borderBottom: `1px solid ${alpha('#94a3b8', 0.14)}`,
              '& .MuiTab-root': {
                minHeight: 38,
                px: 1.4,
                py: 0.65,
                color: '#94a3b8',
                fontWeight: 900,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: '#7dd3fc !important',
              },
            }}
          >
            {OPERATIONS_VIEWS.map((view) => (
              <Tab
                key={view.value}
                value={view.value}
                label={view.label}
              />
            ))}
          </Tabs>

          {!selectedProcessId ? (
            <Alert severity="info" variant="outlined">
              Configura primero un proceso para comenzar a registrar
              expedientes.
            </Alert>
          ) : null}

          {selectedProcessId &&
          activeView === 'cases' ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                bgcolor: 'rgba(2,6,23,0.18)',
                p: 1.25,
              }}
            >
              <Stack spacing={1.15}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(160px, 1fr))',
                      xl: 'repeat(4, minmax(150px, 1fr))',
                    },
                    gap: 1,
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="Etapa"
                    value={selectedStageFilter}
                    onChange={(event) =>
                      setSelectedStageFilter(event.target.value)
                    }
                    fullWidth
                  >
                    <MenuItem value="">Todas</MenuItem>

                    {orderedStages.map((stage) => (
                      <MenuItem
                        key={stage.id}
                        value={stage.id}
                      >
                        {stage.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Área"
                    value={selectedWorkAreaFilter}
                    onChange={(event) =>
                      setSelectedWorkAreaFilter(
                        event.target.value,
                      )
                    }
                    fullWidth
                  >
                    <MenuItem value="">
                      Todas
                    </MenuItem>

                    <MenuItem
                      value={
                        WITHOUT_OPERATIONAL_AREA_VALUE
                      }
                    >
                      Sin área configurada
                    </MenuItem>

                    {operationalWorkAreas.map(
                      (area) => (
                        <MenuItem
                          key={area.id}
                          value={area.id}
                        >
                          {area.name}
                        </MenuItem>
                      ),
                    )}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Prioridad"
                    value={selectedPriorityFilter}
                    onChange={(event) =>
                      setSelectedPriorityFilter(
                        event.target.value as
                          | IndustrialWorkflowCasePriority
                          | '',
                      )
                    }
                    fullWidth
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="low">Baja</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">Alta</MenuItem>
                    <MenuItem value="urgent">Urgente</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Estado de etapa"
                    value={selectedDueStatusFilter}
                    onChange={(event) =>
                      setSelectedDueStatusFilter(
                        event.target.value as DueStatusFilter,
                      )
                    }
                    fullWidth
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="overdue">
                      Fuera de plazo
                    </MenuItem>
                    <MenuItem value="today">
                      Vencen hoy
                    </MenuItem>
                    <MenuItem value="upcoming">
                      Próximos vencimientos
                    </MenuItem>
                    <MenuItem value="without_due">
                      Sin plazo configurado
                    </MenuItem>
                  </TextField>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.82rem',
                    }}
                  >
                    {filteredCases.length}{' '}
                    {filteredCases.length === 1
                      ? 'expediente visible'
                      : 'expedientes visibles'}
                  </Typography>

                  {hasActiveFilters ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResetFilters}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {selectedProcessId &&
          activeView === 'cases' ? (
            <>
              {casesQuery.isLoading ? (
            <Typography sx={{ color: '#94a3b8' }}>
              Cargando expedientes...
            </Typography>
          ) : null}

          {selectedProcessId && casesQuery.isError ? (
            <Alert severity="error" variant="outlined">
              {getErrorMessage(casesQuery.error)}
            </Alert>
          ) : null}

          {selectedProcessId &&
          !casesQuery.isLoading &&
          !casesQuery.isError &&
          !(casesQuery.data ?? []).length ? (
            <Alert severity="info" variant="outlined">
              Todavía no hay expedientes abiertos.
            </Alert>
          ) : null}

          {selectedProcessId &&
          !casesQuery.isLoading &&
          !casesQuery.isError &&
          (casesQuery.data ?? []).length &&
          !filteredCases.length ? (
            <Alert severity="info" variant="outlined">
              No hay expedientes que coincidan con los filtros actuales.
            </Alert>
          ) : null}

          <Stack spacing={1.5}>
            {groupedCases.map((group) => (
              <Paper
                key={group.stage?.id ?? '__unknown_stage__'}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha('#94a3b8', 0.16)}`,
                  bgcolor: 'rgba(15,23,42,0.34)',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.35,
                    py: 0.85,
                    borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
                    bgcolor: 'rgba(2,6,23,0.2)',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 950,
                        fontSize: '0.86rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.55,
                      }}
                    >
                      {group.stage?.name ?? 'Etapa no disponible'}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.72rem',
                        mt: 0.18,
                      }}
                    >
                      {getStageGroupContextLabel(
                        group.stage,
                        group.cases[0],
                      )}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={group.cases.length}
                    sx={{
                      color: '#bae6fd',
                      bgcolor: alpha('#0284c7', 0.16),
                      fontWeight: 850,
                    }}
                  />
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: listMinWidth }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: listGridTemplateColumns,
                        columnGap: 1,
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.8,
                        borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        bgcolor: 'rgba(2,6,23,0.12)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 850,
                          textTransform: 'uppercase',
                        }}
                      >
                        Expediente
                      </Typography>

                      {visibleListFields.map((field) => (
                        <Typography
                          key={field.id}
                          sx={{
                            color: '#64748b',
                            fontSize: '0.7rem',
                            fontWeight: 850,
                            textTransform: 'uppercase',
                          }}
                        >
                          {field.label}
                        </Typography>
                      ))}

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 850,
                          textTransform: 'uppercase',
                        }}
                      >
                        Estado de etapa
                      </Typography>

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 850,
                          textTransform: 'uppercase',
                        }}
                      >
                        Objetivo expediente
                      </Typography>

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.7rem',
                          fontWeight: 850,
                          textTransform: 'uppercase',
                        }}
                      >
                        Prioridad
                      </Typography>

                      <Box />
                    </Box>

                    {group.cases.map((workflowCase) => {
                      const stageTone =
                        getOperationalStageTone(
                          workflowCase,
                        );

                      const targetDateTone =
                        getCaseTargetDateTone(
                          workflowCase.due_at,
                        );

                      return (
                        <Box
                          key={workflowCase.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: listGridTemplateColumns,
                            columnGap: 1,
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.78,
                            borderBottom: `1px solid ${alpha('#94a3b8', 0.08)}`,
                            transition: 'background-color 0.16s ease',
                            '&:last-of-type': {
                              borderBottom: 'none',
                            },
                            '&:hover': {
                              bgcolor: 'rgba(15,23,42,0.62)',
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {workflowCase.title}
                            </Typography>

                            <Typography
                              sx={{
                                color: '#7dd3fc',
                                fontSize: '0.74rem',
                                fontWeight: 800,
                                mt: 0.25,
                              }}
                            >
                              {workflowCase.reference_code}
                            </Typography>
                          </Box>

                          {visibleListFields.map((field) => (
                            <Typography
                              key={field.id}
                              title={formatFieldValue(
                                workflowCase.field_values[field.code],
                              )}
                              sx={{
                                color: '#cbd5e1',
                                fontSize: '0.84rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatFieldValue(
                                workflowCase.field_values[field.code],
                              )}
                            </Typography>
                          ))}

                          <Chip
                            size="small"
                            label={stageTone.label}
                            title={
                              workflowCase
                                .operational_due_at
                                ? `Fecha operativa: ${formatDate(
                                    workflowCase
                                      .operational_due_at,
                                  )}`
                                : undefined
                            }
                            sx={{
                              justifySelf: 'start',
                              maxWidth: 215,
                              color: stageTone.color,
                              bgcolor: stageTone.bgcolor,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                          <Chip
                            size="small"
                            label={targetDateTone.label}
                            title={
                              workflowCase.due_at
                                ? `Fecha objetivo global: ${formatDate(
                                    workflowCase.due_at,
                                  )}`
                                : 'Todavía no se ha definido una fecha objetivo global.'
                            }
                            sx={{
                              justifySelf: 'start',
                              maxWidth: 130,
                              color: targetDateTone.color,
                              bgcolor: targetDateTone.bgcolor,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                          <Chip
                            size="small"
                            label={getPriorityLabel(workflowCase.priority)}
                            sx={{
                              ...getPriorityTone(workflowCase.priority),
                              justifySelf: 'start',
                            }}
                          />

                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewRounded />}
                            onClick={() => setTasksCase(workflowCase)}
                            sx={{
                              justifySelf: 'end',
                              fontWeight: 900,
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Abrir expediente
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
            </>
          ) : null}

          {selectedProcessId &&
          activeView !== 'cases' ? (
            <IndustrialWorkflowsOperationsDashboard
              activeView={activeView}
              dashboard={operationsDashboardQuery.data}
              isLoading={operationsDashboardQuery.isLoading}
              isError={operationsDashboardQuery.isError}
              error={operationsDashboardQuery.error}
              assignableUsers={assignableUsersQuery.data ?? []}
              onOpenCase={(workflowCase) =>
                setTasksCase(workflowCase)
              }
            />
          ) : null}
        </Stack>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseCaseDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: 'rgba(15,23,42,0.78)',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <AssignmentRounded
              sx={{ color: '#38bdf8' }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  fontSize: '1.05rem',
                }}
              >
                {editingCase
                  ? 'Editar datos del expediente'
                  : 'Nuevo expediente'}
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  mt: 0.15,
                }}
              >
                Registra únicamente la información propia del expediente.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: 'rgba(15,23,42,0.34)',
          }}
        >
          <Stack spacing={1.4} sx={{ pt: 1.4 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#38bdf8', 0.16)}`,
                bgcolor: 'rgba(15,23,42,0.5)',
                px: 1.45,
                py: 1.3,
              }}
            >
              <Stack spacing={1.2}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <DescriptionRounded
                    sx={{ color: '#7dd3fc' }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Información principal
                  </Typography>
                </Stack>

                <TextField
                  label="Título"
                  value={caseForm.title}
                  onChange={(event) =>
                    setCaseForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                  helperText="Nombre operativo que permitirá identificar rápidamente el expediente."
                  fullWidth
                  required
                />

                {titleSuggestions.length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                      bgcolor: 'rgba(2,6,23,0.18)',
                      px: 1.15,
                      py: 1,
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Stack
                        direction="row"
                        spacing={0.65}
                        alignItems="center"
                      >
                        <AutoAwesomeRounded
                          sx={{
                            color: '#7dd3fc',
                            fontSize: '1rem',
                          }}
                        />

                        <Typography
                          sx={{
                            color: '#cbd5e1',
                            fontWeight: 850,
                            fontSize: '0.8rem',
                          }}
                        >
                          Atajos de título
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.65}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {titleSuggestions.map((suggestion) => (
                          <Button
                            key={suggestion.key}
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setCaseForm((current) => ({
                                ...current,
                                title: suggestion.value,
                              }))
                            }
                            sx={{
                              textTransform: 'none',
                              fontWeight: 850,
                            }}
                          >
                            {`Usar “${suggestion.value}”`}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}

                <TextField
                  label="Descripción"
                  value={caseForm.description}
                  onChange={(event) =>
                    setCaseForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  helperText="Opcional. Añade contexto solo cuando facilite la gestión del expediente."
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#94a3b8', 0.14)}`,
                bgcolor: 'rgba(15,23,42,0.5)',
                px: 1.45,
                py: 1.3,
              }}
            >
              <Stack spacing={1.1}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <CalendarMonthRounded
                    sx={{ color: '#7dd3fc' }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Planificación del expediente
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                    gap: 1,
                  }}
                >
                  <TextField
                    select
                    label="Prioridad"
                    value={caseForm.priority}
                    onChange={(event) =>
                      setCaseForm((current) => ({
                        ...current,
                        priority:
                          event.target.value as IndustrialWorkflowCasePriority,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="low">Baja</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">Alta</MenuItem>
                    <MenuItem value="urgent">Urgente</MenuItem>
                  </TextField>

                  <IndustrialWorkflowDatePicker
                    label="Fecha objetivo del expediente"
                    value={caseForm.due_at}
                    onChange={(value) =>
                      setCaseForm((current) => ({
                        ...current,
                        due_at: value,
                      }))
                    }
                    helperText="Opcional. Compromiso global del expediente, independiente de los plazos de cada etapa."
                  />
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#94a3b8', 0.14)}`,
                bgcolor: 'rgba(15,23,42,0.5)',
                px: 1.45,
                py: 1.3,
              }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <TuneRounded
                    sx={{ color: '#7dd3fc' }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Datos del expediente
                    </Typography>

                    <Typography
                      sx={{
                        color: '#94a3b8',
                        fontSize: '0.78rem',
                        mt: 0.12,
                      }}
                    >
                      Información configurable del expediente, separada
                      por protagonismo operativo.
                    </Typography>
                  </Box>
                </Stack>

                {activeFields.length ? (
                  <Stack spacing={1.35}>
                    {[
                      {
                        key: 'primary',
                        title: 'Datos principales',
                        description:
                          'Identificación y datos clave visibles del expediente.',
                        fields: activeFields.filter(
                          (field) =>
                            getFieldDisplayTier(field) === 'primary',
                        ),
                      },
                      {
                        key: 'secondary',
                        title: 'Datos secundarios',
                        description:
                          'Información adicional útil para la gestión del expediente.',
                        fields: activeFields.filter(
                          (field) =>
                            getFieldDisplayTier(field) === 'secondary',
                        ),
                      },
                      {
                        key: 'technical',
                        title: 'Datos técnicos',
                        description:
                          'Parámetros técnicos o productivos reutilizables por tareas y partes.',
                        fields: activeFields.filter(
                          (field) =>
                            getFieldDisplayTier(field) === 'technical',
                        ),
                      },
                    ]
                      .filter((group) => group.fields.length)
                      .map((group) => (
                        <Paper
                          key={group.key}
                          elevation={0}
                          sx={{
                            borderRadius: 2.6,
                            border: `1px solid ${alpha(
                              group.key === 'primary'
                                ? '#f59e0b'
                                : group.key === 'technical'
                                  ? '#8b5cf6'
                                  : '#64748b',
                              0.16,
                            )}`,
                            bgcolor: 'rgba(2,6,23,0.18)',
                            px: 1.15,
                            py: 1.05,
                          }}
                        >
                          <Stack spacing={0.95}>
                            <Box>
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 900,
                                  fontSize: '0.9rem',
                                }}
                              >
                                {group.title}
                              </Typography>

                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontSize: '0.76rem',
                                  mt: 0.12,
                                }}
                              >
                                {group.description}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(2, minmax(0, 1fr))',
                                },
                                gap: 1,
                              }}
                            >
                              {group.fields.map((field) => (
                                <Box
                                  key={field.id}
                                  sx={{
                                    gridColumn:
                                      field.field_type === 'long_text'
                                        ? '1 / -1'
                                        : 'auto',
                                  }}
                                >
                                  <DynamicFieldInput
                                    field={field}
                                    value={caseForm.field_values[field.code]}
                                    onChange={(value) =>
                                      setCaseForm((current) => ({
                                        ...current,
                                        field_values: {
                                          ...current.field_values,
                                          [field.code]: value,
                                        },
                                      }))
                                    }
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                  </Stack>
                ) : (
                  <Alert
                    severity="info"
                    variant="outlined"
                  >
                    Todavía no hay campos configurados para este flujo.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: 'rgba(15,23,42,0.78)',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: 1 }}
          >
            {editingCase ? (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteRounded />}
                disabled={
                  createCaseMutation.isPending ||
                  updateCaseMutation.isPending ||
                  deleteCaseMutation.isPending
                }
                onClick={() => {
                  setDeleteReferenceCodeConfirmation('');
                  setCasePendingDelete(editingCase);
                }}
              >
                Eliminar expediente
              </Button>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleCloseCaseDialog}
                disabled={deleteCaseMutation.isPending}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                disabled={
                  createCaseMutation.isPending ||
                  updateCaseMutation.isPending ||
                  deleteCaseMutation.isPending ||
                  !caseForm.title.trim()
                }
                onClick={handleSaveCase}
              >
                Guardar
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(casePendingDelete)}
        onClose={() => {
          if (!deleteCaseMutation.isPending) {
            setCasePendingDelete(null);
            setDeleteReferenceCodeConfirmation('');
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Eliminar expediente
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography>
              Vas a eliminar permanentemente el expediente{' '}
              <strong>
                {casePendingDelete?.title}
              </strong>
              .
            </Typography>

            <Alert
              severity="warning"
              variant="outlined"
            >
              Esta acción no se puede deshacer. También dejarán
              de estar disponibles sus tareas, datos dinámicos
              e historial. Utilízala únicamente para eliminar
              registros erróneos o de prueba.
            </Alert>

            <TextField
              label="Referencia de confirmación"
              value={deleteReferenceCodeConfirmation}
              onChange={(event) =>
                setDeleteReferenceCodeConfirmation(
                  event.target.value,
                )
              }
              helperText={
                casePendingDelete
                  ? `Escribe exactamente: ${casePendingDelete.reference_code}`
                  : undefined
              }
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteCaseMutation.isPending}
            onClick={() => {
              setCasePendingDelete(null);
              setDeleteReferenceCodeConfirmation('');
            }}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={
              deleteCaseMutation.isPending ||
              !casePendingDelete ||
              deleteReferenceCodeConfirmation.trim() !==
                casePendingDelete.reference_code
            }
            onClick={handleConfirmDeleteCase}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <IndustrialWorkflowCaseTasksDialog
        workflowCase={tasksCase}
        stages={stagesQuery.data ?? []}
        assignableUsers={
          assignableUsersQuery.data ?? []
        }
        currentUserId={
          workflowsContextQuery.data?.user_id
          ?? null
        }
        canOperateTasks={canOperateTasks}
        canManageTasks={canManageTasks}
        canManageCases={canManageCases}
        onOpenHistory={() => {
          if (tasksCase) {
            setHistoryCase(tasksCase);
          }
        }}
        onEditCase={() => {
          if (tasksCase) {
            handleEditCase(tasksCase);
          }
        }}
        onStageCompleted={() =>
          setTasksCase(null)
        }
        onClose={() => setTasksCase(null)}
      />

      <IndustrialWorkflowCaseHistoryDialog
        workflowCase={historyCase}
        stages={stagesQuery.data ?? []}
        assignableUsers={
          assignableUsersQuery.data ?? []
        }
        currentUserId={
          workflowsContextQuery.data?.user_id
          ?? null
        }
        onClose={() =>
          setHistoryCase(null)
        }
      />
    </Stack>
  );
}
